import { Injectable } from '@angular/core';
import { UIEntityItem } from '../entity-list/ui-entity-item/ui-entity-item';
import { Subject, Observable } from 'rxjs';
import { TabData } from './entity-details-tab/tab-data';
import { TAB_ACTION } from './entity-details-tab/tab-action';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';

import { AlertErrorDataService } from '../alert/alert-error/alert-error-data.service';
import { ALERT_ERROR_NAME } from '../alert/alert-error/alert-error-name';
import { AlertFlashDataService } from '../alert/alert-flash/alert-flash-data.service';
import { UI_ENTITY_ITEM_ACTION } from '../entity-list/const/ui-entity-item-action';
import { ServiceItem } from '../entity-list/base-entity-item/service-item';

@Injectable({
  providedIn: 'root'
})
export class EntityDetailsDataService {

  private _resetServiceAccess = false;
  private _currentEntityType: string = '';
  private _preventDetailsToClose = false;

  private _readOnly = false;
  private _routerUrl = '';

  private _closeDetails = new Subject<boolean>();
  public closeDetails$: Observable<boolean> = this._closeDetails.asObservable();

  private _uiEntityItem = new Subject<UIEntityItem>();
  public uiEntityItem$: Observable<UIEntityItem> = this._uiEntityItem.asObservable();

  private _selectedUIEntityItem = new Subject<UIEntityItem | null>();
  public selectedUIEntityItem$: Observable<UIEntityItem | null> = this._selectedUIEntityItem.asObservable();

  private _detailsDialog = new Subject<string>();
  public detailsDialog$: Observable<string> = this._detailsDialog.asObservable();

  private _discardChangesDialog = new Subject<string>();
  public discardChangesDialog$: Observable<string> = this._discardChangesDialog.asObservable();

  private _confirmationDialog = new Subject<string>();
  public confirmationDialog$: Observable<string> = this._confirmationDialog.asObservable();

  private _refreshServiceAccess = new Subject<boolean>();
  public refreshServiceAccess$: Observable<boolean> = this._refreshServiceAccess.asObservable();

  private _tab = new Subject<TabData>();
  public tab$: Observable<TabData> = this._tab.asObservable();

  private _refreshMembersGrid = new Subject<boolean>();
  public refreshMembersGrid$: Observable<boolean> = this._refreshMembersGrid.asObservable();

  private _includeAllMembers = new Subject<boolean>();
  public includeAllMembers$: Observable<boolean> = this._includeAllMembers.asObservable();

  private _makeItReadOnly = new Subject<boolean>();
  public makeItReadOnly$: Observable<boolean> = this._makeItReadOnly.asObservable();

  private _removeReadOnly = new Subject<boolean>();
  public removeReadOnly$: Observable<boolean> = this._removeReadOnly.asObservable();

  private _password = new Subject<string>();
  public password$: Observable<string> = this._password.asObservable();

  private _resetEditedItem = new Subject<boolean>();
  public resetEditedItem$: Observable<boolean> = this._resetEditedItem.asObservable();

  private _spinner = new Subject<boolean>();
  public spinner$: Observable<boolean> = this._spinner.asObservable();

  constructor(private alertFlashDataService: AlertFlashDataService, private alertErrorDataService: AlertErrorDataService) { }

  showSpinner(): void {
    this._spinner.next(true);
  }
  
  hideSpinner(): void {
    this._spinner.next(false);
  }

  resetEditedEntity(): void {
    this._resetEditedItem.next(true);
  }

  closeEntityDetails(): void {
    this._closeDetails.next(true);
  }

  isPreventDetailsToClose(): boolean {
    return this._preventDetailsToClose;
  }

  clearPreventDetailsToClose(): void {
    this._preventDetailsToClose = false;
  }

  getIncludeAllMembersSessionStorageKey(type: string, id: string, memberType?: string): string {
    if (memberType && memberType.length) {
      return `${type}_${id}_${memberType}`;
    }
    return `${type}_${id}`;
  }

  sendPassword(password: string): void {
    this._password.next(password);
  }

  includeAllMembers(): void {
    this._includeAllMembers.next(true);
  }

  isReadOnly(): boolean {
    return this._readOnly;
  }

  setReadOnly(value: boolean) {
    this._readOnly = value;
  }

  makeItReadOnly(): void {
    this._makeItReadOnly.next(true);
  }

  removeReadOnly(): void {
    this._removeReadOnly.next(true);
  }

  get routerUrl(): string {
    return this._routerUrl;
  }

  set routerUrl(value: string) {
    this._routerUrl = value;
  }

  isResetServiceAccess(): boolean {
    return this._resetServiceAccess;
  }

  setResetServiceAccess(value: boolean) {
    this._resetServiceAccess = value;
  }

  get currentEntityType(): string {
    return this._currentEntityType;
  }

  set currentEntityType(type: string) {
    this._currentEntityType = type;
  }

  clickOnUIEntityItem(uiEntityItem: UIEntityItem): void {
    this._selectedUIEntityItem.next(uiEntityItem);
  }

  clearSelectedUIEntityItem(): void {
    this._preventDetailsToClose = false;
    this._selectedUIEntityItem.next(null);
  }

  createOrUpdateUIEntityItem(uiEntityItem: UIEntityItem): void {
    this._uiEntityItem.next(uiEntityItem);
  }

  deleteUIEntityItem(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.delete;
    this._uiEntityItem.next(uiEntityItem);
  }

  changeCompanyStatus(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.changeStatus;
    this._uiEntityItem.next(uiEntityItem);
  }

  preventTabChange(): void {
    this._preventDetailsToClose = true;
    this._tab.next({ action: TAB_ACTION.preventChange, tabIndex: -1 });
  }

  allowTabChange(): void {
    this._preventDetailsToClose = false;
    this._tab.next({ action: TAB_ACTION.allowChange, tabIndex: -1 });
  }

  showTab(tabIndex: number): void {
    this._tab.next({ action: TAB_ACTION.show, tabIndex: tabIndex });
  }

  showDetailsDialog(): void {
    this._detailsDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDetailsDialog(): void {
    this.alertErrorDataService.alertErrorName = ALERT_ERROR_NAME.main;
    this._currentEntityType = '';
    this.alertFlashDataService.reset();
    this.alertErrorDataService.reset();
    this._tab.next({ action: TAB_ACTION.resetActive, tabIndex: -1 });
    this._detailsDialog.next(DIALOG_VISIBILITY.hide);
  }

  showDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.hide);
  }

  showConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.show);
  }

  hideConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideConfirmationDialogAndRefresh(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  refreshServiceAccess(): void {
    this._refreshServiceAccess.next(true);
  }

  refreshMembersGrid(): void {
    this._refreshMembersGrid.next(true);
  }

  copyInputMessage(event: Event) {
    const span = event.currentTarget as HTMLSpanElement;
    const input = span.previousElementSibling as HTMLInputElement;
    navigator.clipboard.writeText(input.value);
  }

}
